import React, { useState } from 'react';
import { I18n } from 'react-redux-i18n';

import { useSession } from '../../../hooks/useSession';
import { useOrgInfo } from '../../../hooks/useOrgInfo';
import { useAppSelector } from '../../../hooks/redux/useApp';
import { giftAidStatus } from '../../../pages/organization/swiftaidConnection/constants';
import { AnnouncementBanner } from '../../common/announcementBanner';
import { selectIsFeatureFlagExplicitlyEnabled } from '../../../modules/featureFlags/featureFlagSelectors';
import { FeatureFlagKeys } from '../../../modules/featureFlags/featureFlagTypes';
import { isSnoozed, snooze } from './giftAidNudgeSnooze';

const i18nPath = 'template.organisation.content.finance.swiftaid.nudge';

/**
 * Upside framed reminder for an organisation admin whose organisation has an
 * HMRC reference but no Swiftaid link yet (`giftAidStatus` of `notLinked`).
 * Shown on every page until dismissed for 30 days or the link exists. Reads the
 * cached organisation info rather than any page's record, so it works wherever
 * the admin lands, and goes away without a reload once the Swiftaid panel or
 * another tab records the link.
 *
 * Gated behind `FeatureFlagKeys.GiftAidNudge` until Grassrootz's own Gift Aid
 * agent registration with HMRC is approved; the flag stays off until then. It
 * also needs `FeatureFlagKeys.SwiftaidConnect`, since the link lands on the
 * Swiftaid panel and would lead nowhere while that is hidden.
 */
export const GiftAidNudge = () => {
  const session = useSession();
  const orgInfo = useOrgInfo();
  const isGiftAidNudgeEnabled = useAppSelector(
    (state) =>
      selectIsFeatureFlagExplicitlyEnabled(
        state,
        FeatureFlagKeys.GiftAidNudge
      ) &&
      selectIsFeatureFlagExplicitlyEnabled(
        state,
        FeatureFlagKeys.SwiftaidConnect
      )
  );
  // Session.organization is untyped. Only the id is needed, as a string key.
  const orgId: unknown = session.organization?.id;
  const orgIdKey =
    typeof orgId === 'number' || typeof orgId === 'string'
      ? String(orgId)
      : null;
  // Keyed by organisation rather than a bare flag so an impersonation switch
  // does not carry a dismissal across to a different organisation.
  const [dismissedOrgId, setDismissedOrgId] = useState<string | null>(null);

  const visible =
    isGiftAidNudgeEnabled &&
    session.isOrganizationAdmin &&
    !session.isSystemAdmin &&
    orgIdKey !== null &&
    orgInfo !== null &&
    String(orgInfo.orgId) === orgIdKey &&
    orgInfo.giftAidStatus === giftAidStatus.notLinked &&
    dismissedOrgId !== orgIdKey &&
    !isSnoozed(orgIdKey);

  if (!visible || orgIdKey === null) {
    return null;
  }

  const onDismiss = () => {
    snooze(orgIdKey);
    setDismissedOrgId(orgIdKey);
  };

  return (
    <AnnouncementBanner
      testId="gift-aid-nudge"
      title={I18n.t(`${i18nPath}.heading`)}
      body={I18n.t(`${i18nPath}.body`)}
      link={{
        label: I18n.t(`${i18nPath}.link`),
        to: `/organizations/edit/${orgIdKey}/finance`
      }}
      onDismiss={onDismiss}
      dismissLabel={I18n.t(`${i18nPath}.dismiss`)}
      dismissTestId="gift-aid-nudge-dismiss"
    />
  );
};

export default GiftAidNudge;
